import React, { useEffect, useRef, useState } from "react";
import { Alert, Button, Radio } from "antd";
import { EnvironmentOutlined } from "@ant-design/icons";
import { useFormikContext } from "formik";
import { INewOrderFormValues } from "../../../types/user";
import {
  addressQuery,
  DeliveryAddress,
  GeocodedAddress,
  mapUrls,
  searchDeliveryAddress,
} from "../../../services/geocoding-service";

interface Lookup {
  addressKey: string;
  results: GeocodedAddress[];
  selectedId: string;
}

const addressIdentity = (address: DeliveryAddress): string =>
  JSON.stringify([
    address.street,
    address.streetNumber,
    address.city,
    address.state,
  ]);

const DeliveryAddressMap = (): JSX.Element => {
  const {
    values,
    setValues,
    setFieldValue,
    setFieldError,
    errors,
  } = useFormikContext<INewOrderFormValues>();
  const query = addressQuery(values);
  const addressKey = addressIdentity(values);
  const canSearch = Boolean(values.street?.trim() && values.city?.trim());
  const [lookup, setLookup] = useState<Lookup | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const requestId = useRef(0);
  const controller = useRef<AbortController | null>(null);
  const confirmedAddress = useRef<string | null>(null);

  useEffect(() => {
    setLookup((current) =>
      current?.addressKey === addressKey ? current : null
    );
    if (confirmedAddress.current !== addressKey) {
      confirmedAddress.current = null;
      setFieldValue("geo", null, false);
      setFieldError("geo", undefined);
    }
    setLoading(false);
    setError("");
    return () => {
      requestId.current += 1;
      controller.current?.abort();
    };
  }, [addressKey, setFieldValue, setFieldError]);

  const currentLookup = lookup?.addressKey === addressKey ? lookup : null;
  const selected = currentLookup?.results.find(
    (result) => result.id === currentLookup.selectedId
  );
  const urls = selected ? mapUrls(selected) : null;
  const isConfirmed = Boolean(
    selected &&
      confirmedAddress.current === addressKey &&
      values.geo?.lat === selected.latitude &&
      values.geo?.lon === selected.longitude
  );

  const clearLocation = (): void => {
    confirmedAddress.current = null;
    setFieldValue("geo", null, false);
    setFieldError("geo", undefined);
  };

  const search = async (): Promise<void> => {
    if (!canSearch || loading) return;
    clearLocation();
    controller.current?.abort();
    const activeController = new AbortController();
    controller.current = activeController;
    requestId.current += 1;
    const activeRequest = requestId.current;
    setLookup(null);
    setError("");
    setLoading(true);
    const timeout = window.setTimeout(() => activeController.abort(), 10000);
    activeController.signal.addEventListener(
      "abort",
      () => window.clearTimeout(timeout),
      { once: true }
    );

    try {
      const results = await searchDeliveryAddress(
        query,
        activeController.signal
      );
      if (requestId.current === activeRequest) {
        setLookup({ addressKey, results, selectedId: results[0]?.id || "" });
      }
    } catch (reason) {
      if (requestId.current === activeRequest) {
        setError(
          reason instanceof Error && !activeController.signal.aborted
            ? reason.message
            : "Nie udało się połączyć z wyszukiwarką adresów. Spróbuj ponownie."
        );
      }
    } finally {
      window.clearTimeout(timeout);
      if (requestId.current === activeRequest) setLoading(false);
    }
  };

  const useAddress = (): void => {
    if (!selected?.isBuilding || !currentLookup) return;
    const updated = {
      ...values,
      street: selected.street,
      streetNumber: selected.streetNumber,
      city: selected.city,
      state: selected.state || values.state,
      geo: { lat: selected.latitude, lon: selected.longitude },
    };
    confirmedAddress.current = addressIdentity(updated);
    setLookup({ ...currentLookup, addressKey: confirmedAddress.current });
    setFieldError("geo", undefined);
    setValues(updated);
  };

  return (
    <section className="delivery-address-map" aria-label="Lokalizacja dostawy">
      <Button
        block
        htmlType="button"
        icon={<EnvironmentOutlined aria-hidden />}
        loading={loading}
        disabled={!canSearch}
        onClick={search}
      >
        Znajdź na mapie
      </Button>
      <p className="delivery-address-map__hint">
        Wpisz ulicę i miasto. Numer budynku pozwoli dokładniej wskazać adres.
      </p>

      <div role="status" aria-live="polite">
        {errors.geo && (
          <Alert
            type="error"
            showIcon
            message="Nieprawidłowa lokalizacja dostawy."
            description="Wyszukaj adres ponownie i zatwierdź właściwy punkt."
          />
        )}
        {error && (
          <Alert
            type="warning"
            showIcon
            message={error}
            description="Możesz nadal dodać dostawę z wpisanym adresem."
          />
        )}
        {currentLookup?.results.length === 0 && (
          <Alert
            type="info"
            showIcon
            message="Nie znaleziono adresu na mapie."
            description="Sprawdź ulicę, numer i miasto lub dodaj dostawę z wpisanym adresem."
          />
        )}
      </div>

      {currentLookup && currentLookup.results.length > 0 && (
        <Radio.Group
          className="delivery-address-map__results"
          aria-label="Znalezione adresy"
          value={currentLookup.selectedId}
          onChange={(event) => {
            clearLocation();
            setLookup({ ...currentLookup, selectedId: event.target.value });
          }}
        >
          {currentLookup.results.map((result) => (
            <Radio key={result.id} value={result.id}>
              {result.label}
              {!result.isBuilding && (
                <span className="delivery-address-map__precision">
                  Lokalizacja przybliżona
                </span>
              )}
            </Radio>
          ))}
        </Radio.Group>
      )}

      {selected && urls && (
        <div className="delivery-address-map__preview">
          {!selected.isBuilding && (
            <Alert
              type="warning"
              showIcon
              message="Mapa pokazuje okolicę, a nie konkretny budynek."
              description="Sprawdź numer budynku we wpisanym adresie."
            />
          )}
          <iframe
            title="Podgląd adresu dostawy na OpenStreetMap"
            src={urls.embed}
            loading="lazy"
            referrerPolicy="no-referrer"
          />
          <div className="delivery-address-map__details">
            <span>
              GPS: {selected.latitude.toFixed(6)},{" "}
              {selected.longitude.toFixed(6)}
            </span>
            <a href={urls.external} target="_blank" rel="noopener noreferrer">
              Otwórz większą mapę
            </a>
          </div>
          {selected.isBuilding && (
            <Button htmlType="button" block onClick={useAddress}>
              Użyj tego adresu w formularzu
            </Button>
          )}
          {isConfirmed && (
            <p className="delivery-address-map__hint" role="status">
              Ta lokalizacja zostanie zapisana z dostawą.
            </p>
          )}
        </div>
      )}
      <p className="delivery-address-map__attribution">
        Dane ©{" "}
        <a
          href="https://www.openstreetmap.org/copyright"
          target="_blank"
          rel="noopener noreferrer"
        >
          OpenStreetMap
        </a>
        {" · "}
        <a
          href="https://photon.komoot.io"
          target="_blank"
          rel="noopener noreferrer"
        >
          Photon
        </a>
      </p>
    </section>
  );
};

export default DeliveryAddressMap;
